import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2, Check } from "lucide-react";

export default function ContactPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
  });

  useEffect(() => {
    api.get("/contact/settings")
      .then(({ data }) => {
        setSettings(data);
        setForm((f) => ({ ...f, subject: f.subject || (data.subjects || [])[0] || "Other" }));
      })
      .catch(() => setSettings(null));
  }, []);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
      }));
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return; // ignore double-click while in-flight
    if (!form.name || !form.email || !form.message || !form.subject) {
      toast.error("Name, email, subject, and message are required.");
      return;
    }
    if ((form.message || "").trim().length < 10) {
      toast.error("Message must be at least 10 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message received — we'll be in touch.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!settings) {
    return <div className="py-10 text-[#F5F2E8]/60">Loading…</div>;
  }

  return (
    <div className="py-10 max-w-5xl mx-auto" data-testid="contact-page">
      {/* Hero */}
      <div className="overline">Get in touch</div>
      <h1 className="font-display uppercase text-5xl md:text-6xl tracking-tight mt-2 leading-[0.95]">
        {settings.headline?.split(" ")[0]}{" "}
        <span style={{ color: "#76B82A" }}>
          {settings.headline?.split(" ").slice(1).join(" ") || "us"}
        </span>
      </h1>
      <p className="text-[#F5F2E8]/75 mt-4 max-w-2xl text-lg">
        {settings.subhead}
      </p>

      <div className="grid md:grid-cols-3 gap-6 mt-10">
        {/* Direct contact card */}
        <aside className="loot-card-flat p-5 space-y-4 md:order-2" data-testid="contact-info-card">
          <div>
            <div className="overline mb-1">Direct</div>
            <p className="text-[#F5F2E8]/70 text-sm">{settings.intro}</p>
          </div>
          {settings.show_email && settings.public_email && (
            <a
              href={`mailto:${settings.public_email}?subject=The%20Local%20Loot`}
              className="flex items-start gap-3 hover:text-[#76B82A] transition-colors"
              data-testid="contact-email-link"
            >
              <Mail size={16} className="mt-0.5 text-[#76B82A]" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-[#F5F2E8]/60">Email</div>
                <div className="text-sm">{settings.public_email}</div>
              </div>
            </a>
          )}
          {settings.show_phone && settings.public_phone && (
            <a
              href={`tel:${settings.public_phone.replace(/[^0-9+]/g, "")}`}
              className="flex items-start gap-3 hover:text-[#76B82A] transition-colors"
              data-testid="contact-phone-link"
            >
              <Phone size={16} className="mt-0.5 text-[#76B82A]" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-[#F5F2E8]/60">Phone</div>
                <div className="text-sm">{settings.public_phone}</div>
              </div>
            </a>
          )}
          {settings.show_city && settings.public_city && (
            <div className="flex items-start gap-3" data-testid="contact-city">
              <MapPin size={16} className="mt-0.5 text-[#76B82A]" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-[#F5F2E8]/60">Based in</div>
                <div className="text-sm">{settings.public_city}</div>
              </div>
            </div>
          )}
          <div className="border-t border-[#F5F2E8]/10 pt-3 text-xs font-mono text-[#F5F2E8]/50">
            Hosting an event? Skip ahead and{" "}
            <Link to="/private/sales" className="text-[#D7A62A] hover:underline">book a private hunt</Link>.
          </div>
        </aside>

        {/* Form */}
        <form onSubmit={submit} className="md:col-span-2 loot-card-flat p-6 space-y-3 md:order-1">
          {done ? (
            <div className="text-center py-12" data-testid="contact-success">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4" style={{ background: "rgba(118,184,42,0.15)" }}>
                <Check size={24} style={{ color: "#76B82A" }} />
              </div>
              <h2 className="font-display uppercase text-2xl">Message received</h2>
              <p className="text-[#F5F2E8]/70 mt-2 max-w-sm mx-auto">
                We'll get back to you shortly{settings.public_email && <> at <span className="text-[#76B82A]">{form.email}</span></>}.
              </p>
              <button
                type="button"
                onClick={() => { setDone(false); setForm({ ...form, subject: settings.subjects[0] || "Other", message: "", company: "", phone: "" }); }}
                className="btn-ghost mt-5 text-xs"
                data-testid="contact-send-another"
              >
                Send another
              </button>
            </div>
          ) : (
            <>
              <div className="overline mb-2">Send a message</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  className="loot-input"
                  placeholder="Your name *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="contact-name"
                  required
                />
                <input
                  className="loot-input"
                  type="email"
                  placeholder="Email *"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="contact-email"
                  required
                />
                <input
                  className="loot-input"
                  placeholder="Phone (optional)"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="contact-phone"
                />
                <input
                  className="loot-input"
                  placeholder="Company / family (optional)"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  data-testid="contact-company"
                />
                <select
                  className="loot-input md:col-span-2"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  data-testid="contact-subject"
                >
                  {(settings.subjects || []).map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <textarea
                  className="loot-input md:col-span-2"
                  rows={6}
                  placeholder="Your message *"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message"
                  required
                />
              </div>
              <div className="flex items-center justify-between gap-3 pt-2">
                <div className="font-mono text-[10px] text-[#F5F2E8]/50">
                  We never share your details. Replies usually within 1 business day.
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  className="btn-loot inline-flex items-center gap-2"
                  data-testid="contact-submit"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {busy ? "Sending…" : "Send message"}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
